import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';

interface TopicCoverageAnalyticsProps {
  courseId: string;
}

const TopicCoverageAnalytics: React.FC<TopicCoverageAnalyticsProps> = ({ courseId }) => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState('monthly');

  // Mock data
  const topicCompletionRates = [
    { topic: 'Linear Equations', completionRate: 92, estimatedTime: 45, actualTime: 42, abandonment: 3 },
    { topic: 'Quadratic Functions', completionRate: 85, estimatedTime: 60, actualTime: 58, abandonment: 5 },
    { topic: 'Polynomials', completionRate: 78, estimatedTime: 50, actualTime: 55, abandonment: 8 },
    { topic: 'Trigonometry', completionRate: 72, estimatedTime: 70, actualTime: 85, abandonment: 12 },
    { topic: 'Calculus Basics', completionRate: 68, estimatedTime: 80, actualTime: 95, abandonment: 15 },
  ];

  const timeComparison = topicCompletionRates.map(topic => ({
    topic: topic.topic,
    estimated: topic.estimatedTime,
    actual: topic.actualTime,
    difference: topic.actualTime - topic.estimatedTime,
  }));

  const abandonmentData = topicCompletionRates.map(topic => ({
    topic: topic.topic,
    abandonment: topic.abandonment,
  }));

  const coverageStats = {
    totalTopics: 15,
    topicsCovered: 12,
    coveragePercentage: 80,
    averageTopicsPerStudent: 10.5,
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          Topic Coverage Analysis
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Date Range</InputLabel>
          <Select
            value={dateRange}
            label="Date Range"
            onChange={(e) => setDateRange(e.target.value)}
          >
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="all">All Time</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        {/* Overview Cards */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Topics
              </Typography>
              <Typography variant="h4">{coverageStats.totalTopics}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Topics Covered
              </Typography>
              <Typography variant="h4">{coverageStats.topicsCovered}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Coverage %
              </Typography>
              <Typography variant="h4">{coverageStats.coveragePercentage}%</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Avg Topics/Student
              </Typography>
              <Typography variant="h4">{coverageStats.averageTopicsPerStudent}</Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Topic Completion Rates */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Topic Completion Rates
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topicCompletionRates}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="topic" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="completionRate" fill="#8884d8" name="Completion Rate %" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Abandonment Rates */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Topic Abandonment Rates
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={abandonmentData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="topic" type="category" width={100} />
                <Tooltip />
                <Bar dataKey="abandonment" fill="#ff6b6b" name="Abandonment %" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Time Comparison */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Estimated vs Actual Time Spent
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={timeComparison}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="topic" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="estimated" fill="#8884d8" name="Estimated (min)" />
                <Bar dataKey="actual" fill="#82ca9d" name="Actual (min)" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Topic Details Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Topic Performance Details
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate(`/admin/courses/${courseId}/topics`)}
              >
                Manage Topics
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Topic</TableCell>
                    <TableCell align="right">Completion Rate</TableCell>
                    <TableCell align="right">Estimated Time</TableCell>
                    <TableCell align="right">Actual Time</TableCell>
                    <TableCell align="right">Time Diff</TableCell>
                    <TableCell align="right">Abandonment</TableCell>
                    <TableCell align="right">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topicCompletionRates.map((topic, index) => {
                    const timeDiff = topic.actualTime - topic.estimatedTime;
                    const isOverTime = timeDiff > 10;
                    const isUnderTime = timeDiff < -10;
                    return (
                      <TableRow key={index}>
                        <TableCell>{topic.topic}</TableCell>
                        <TableCell align="right">{topic.completionRate}%</TableCell>
                        <TableCell align="right">{topic.estimatedTime} min</TableCell>
                        <TableCell align="right">{topic.actualTime} min</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${timeDiff > 0 ? '+' : ''}${timeDiff} min`}
                            color={isOverTime ? 'error' : isUnderTime ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">{topic.abandonment}%</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={topic.completionRate >= 80 ? 'Good' : topic.completionRate >= 70 ? 'Fair' : 'Needs Attention'}
                            color={topic.completionRate >= 80 ? 'success' : topic.completionRate >= 70 ? 'warning' : 'error'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TopicCoverageAnalytics;

